"use client";

import { ArrowRight, ShieldCheck } from "lucide-react";
import Link from "next/link";

import { Button } from "@/lib/components/button";
import { NumberTicker } from "@/lib/components/number-ticker";

import { useI18n } from "@llmgateway/i18n/react";

import { selectReliabilityText } from "./i18n";

interface StatProps {
	value: number;
	suffix?: string;
	prefix?: string;
	label: string;
	delay?: number;
	decimalPlaces?: number;
}

function Stat({
	value,
	suffix = "",
	prefix = "",
	label,
	delay,
	decimalPlaces,
}: StatProps) {
	return (
		<div className="flex flex-col items-center rounded-2xl border border-border bg-card/50 p-6 backdrop-blur-sm transition-all duration-300 hover:border-primary/50">
			<div className="flex items-baseline justify-center whitespace-nowrap text-xl font-bold text-primary sm:text-2xl lg:text-3xl">
				{prefix}
				<NumberTicker
					value={value}
					delay={delay}
					decimalPlaces={decimalPlaces}
					className="text-primary"
				/>
				{suffix}
			</div>
			<span className="mt-2 text-center text-sm font-medium text-muted-foreground">
				{label}
			</span>
		</div>
	);
}

export function ReliabilityHero() {
	const { locale } = useI18n();
	const text = selectReliabilityText(locale).hero;

	return (
		<section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="mx-auto max-w-4xl text-center">
					<div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5">
						<span className="relative flex h-2 w-2">
							<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
							<span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
						</span>
						<span className="text-xs font-mono text-emerald-600 dark:text-emerald-400">
							{text.eyebrow}
						</span>
						<span className="text-xs text-muted-foreground">
							{text.eyebrowDetail}
						</span>
					</div>
					<h1 className="mb-6 text-4xl font-bold tracking-tight text-balance sm:text-6xl lg:text-7xl">
						{text.title}
					</h1>
					<p className="mx-auto mb-10 max-w-3xl text-lg leading-relaxed text-balance text-muted-foreground sm:text-xl">
						{text.description}
					</p>
					<div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
						<Button size="lg" className="w-full sm:w-auto" asChild>
							<Link href="/signup">
								{text.primaryCta}
								<ArrowRight className="ml-2 h-4 w-4" />
							</Link>
						</Button>
						<Button
							size="lg"
							variant="outline"
							className="w-full bg-transparent sm:w-auto"
							asChild
						>
							<Link href="/enterprise#contact">
								<ShieldCheck className="mr-2 h-4 w-4" />
								{text.secondaryCta}
							</Link>
						</Button>
					</div>

					<div className="mt-16 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:gap-6">
						<Stat
							value={99.9999}
							suffix="%"
							label={text.stats.uptime}
							decimalPlaces={4}
						/>
						<Stat
							value={32}
							prefix="<"
							suffix="s"
							label={text.stats.downtime}
							delay={0.1}
						/>
						<Stat
							value={25}
							suffix="+"
							label={text.stats.providers}
							delay={0.2}
						/>
						<Stat
							value={0}
							suffix="ms"
							label={text.stats.overhead}
							delay={0.3}
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
